import { getAbTestingData } from '@/api/public';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';

import { GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY, GUEST_CHECKOUT_EXPERIMENT_NAME } from './types';
import type {
  GuestCheckoutAssignment,
  GuestCheckoutAssignmentOptions,
  GuestCheckoutExposureEntry,
  GuestCheckoutExperimentValue,
  GuestCheckoutRegion,
  GuestCheckoutStorage,
} from './types';

const inFlightAssignments = new Map<string, Promise<GuestCheckoutAssignment>>();
const memoryAssignments = new Map<string, GuestCheckoutExperimentValue>();

export function serializeGuestCheckoutAssignmentCookie(value: GuestCheckoutExperimentValue) {
  return `${GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY}=${value}; Path=/; SameSite=Lax`;
}

function persistServerReadableAssignment(value: GuestCheckoutExperimentValue) {
  if (typeof globalThis.document === 'undefined') return;

  globalThis.document.cookie = serializeGuestCheckoutAssignmentCookie(value);
}

function getBrowserSessionStorage(): GuestCheckoutStorage | null {
  try {
    if (typeof globalThis.sessionStorage === 'undefined') return null;
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function getRegion(siteName = env.NEXT_PUBLIC_SITE_NAME): GuestCheckoutRegion {
  return siteName === 'us' || siteName === 'ca' ? 'north_america' : 'other_locale';
}

function normalizeAssignment(value: unknown): GuestCheckoutExperimentValue | null {
  if (value === 1 || value === 2) return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim();
  if (normalized === '1') return 1;
  if (normalized === '2') return 2;
  return null;
}

function readCachedAssignment(storage: GuestCheckoutStorage | null) {
  const memoryValue = memoryAssignments.get(GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY) ?? null;
  if (!storage) return memoryValue;

  try {
    return normalizeAssignment(storage.getItem(GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY));
  } catch (error) {
    logger.warn('Guest checkout assignment storage read failed', error);
    return memoryValue;
  }
}

function readDirectPaymentAssignment(storage: GuestCheckoutStorage | null) {
  if (!storage) return null;

  try {
    return normalizeAssignment(storage.getItem(GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY));
  } catch (error) {
    logger.warn('Guest checkout assignment storage read failed', error);
    return null;
  }
}

function persistAssignment(
  value: GuestCheckoutExperimentValue,
  storage: GuestCheckoutStorage | null,
  mirrorToCookie: boolean,
) {
  memoryAssignments.set(GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY, value);

  if (!storage) return;

  try {
    storage.setItem(GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY, String(value));
    if (mirrorToCookie) {
      persistServerReadableAssignment(value);
    }
  } catch (error) {
    logger.warn('Guest checkout assignment storage write failed', error);
  }
}

function createAssignment(
  experimentValue: GuestCheckoutExperimentValue,
  region: GuestCheckoutRegion,
  exposureEntry: GuestCheckoutExposureEntry,
): GuestCheckoutAssignment {
  return {
    experimentName: GUEST_CHECKOUT_EXPERIMENT_NAME,
    experimentValue,
    storageKey: GUEST_CHECKOUT_ASSIGNMENT_STORAGE_KEY,
    region,
    exposureEntry,
    assignmentReady: true,
    assignedAt: Date.now(),
  };
}

function shouldRefreshAssignment(
  entry: GuestCheckoutExposureEntry,
  cachedValue: GuestCheckoutExperimentValue | null,
) {
  return (
    entry === 'cart_view' ||
    entry === 'header_cart_hover' ||
    (entry === 'checkout_click' && cachedValue === null)
  );
}

async function resolveAssignment(
  exposureEntry: GuestCheckoutExposureEntry,
  options: GuestCheckoutAssignmentOptions,
): Promise<GuestCheckoutAssignment> {
  const storage = options.storage === undefined ? getBrowserSessionStorage() : options.storage;
  const mirrorToCookie = options.storage === undefined;
  const region = getRegion(options.siteName);

  if (exposureEntry === 'direct_payment') {
    const directAssignment = readDirectPaymentAssignment(storage);
    if (mirrorToCookie && directAssignment !== null) {
      persistServerReadableAssignment(directAssignment);
    }
    return createAssignment(directAssignment ?? 1, region, exposureEntry);
  }

  const cachedValue = readCachedAssignment(storage);
  const shouldRefresh = shouldRefreshAssignment(exposureEntry, cachedValue);
  if (!shouldRefresh && cachedValue !== null) {
    const storedAssignment = mirrorToCookie ? readDirectPaymentAssignment(storage) : null;
    if (storedAssignment !== null) {
      persistServerReadableAssignment(storedAssignment);
    }
    return createAssignment(cachedValue, region, exposureEntry);
  }

  const fallbackValue: GuestCheckoutExperimentValue = region === 'north_america' ? 1 : 2;

  if (region === 'other_locale' || exposureEntry === 'fallback') {
    persistAssignment(fallbackValue, storage, mirrorToCookie);
    return createAssignment(fallbackValue, region, exposureEntry);
  }

  try {
    const response = await getAbTestingData({
      param_name: GUEST_CHECKOUT_EXPERIMENT_NAME,
    });
    const responseValue = normalizeAssignment(response?.data?.strategy_variable);
    const experimentValue = response?.code === 200 && responseValue ? responseValue : fallbackValue;

    if (!responseValue || response?.code !== 200) {
      logger.warn('Guest checkout assignment response invalid');
    }

    persistAssignment(experimentValue, storage, mirrorToCookie);
    return createAssignment(experimentValue, region, exposureEntry);
  } catch (error) {
    logger.warn('Guest checkout assignment request failed', error);
    persistAssignment(fallbackValue, storage, mirrorToCookie);
    return createAssignment(fallbackValue, region, exposureEntry);
  }
}

export function resolveGuestCheckoutAssignment(
  exposureEntry: GuestCheckoutExposureEntry,
  options: GuestCheckoutAssignmentOptions = {},
): Promise<GuestCheckoutAssignment> {
  const requestKey = `${options.siteName ?? env.NEXT_PUBLIC_SITE_NAME ?? 'unknown'}:${exposureEntry}`;
  const inFlight = inFlightAssignments.get(requestKey);
  if (inFlight) return inFlight;

  const request = resolveAssignment(exposureEntry, options).finally(() => {
    inFlightAssignments.delete(requestKey);
  });
  inFlightAssignments.set(requestKey, request);
  return request;
}

export function normalizeGuestCheckoutExperimentValue(value: unknown) {
  return normalizeAssignment(value);
}
